import React from "react";
import { Row, Col, Typography, Card } from "antd";
import { UserOutlined, TeamOutlined, ArrowRightOutlined } from '@ant-design/icons';
import styled from "styled-components";

const { Title, Text } = Typography;

// Styled Container
const JobBoardContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 80px 20px;
  text-align: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;

  @media (max-width: 768px) {
    padding: 60px 15px;
  }

  .main-title {
    @media (max-width: 768px) {
      font-size: 32px !important;
    }
    @media (max-width: 480px) {
      font-size: 28px !important;
    }
  }

  .main-subtitle {
    @media (max-width: 768px) {
      font-size: 18px !important;
    }
    @media (max-width: 480px) {
      font-size: 16px !important;
    }
  }

  .section-title {
    @media (max-width: 768px) {
      font-size: 28px !important;
    }
    @media (max-width: 480px) {
      font-size: 24px !important;
    }
  }
`;

// Styled Card for dual flows
const FlowCard = styled(Card)`
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.95);
  border: none;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  color: #333;
  text-align: center;
  padding: 32px 24px;
  height: 100%;
  
  transition: transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 16px 48px rgba(0, 0, 0, 0.15);
  }

  @media (max-width: 768px) {
    padding: 24px 16px;
  }
`;

// Step Card for dual flows
const StepCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 12px 16px;
  margin: 12px 0;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  text-align: left;

  @media (max-width: 768px) {
    padding: 14px 16px;
    margin: 10px 0;
  }
`;

const JobBoardSection = () => {
  return (
    <JobBoardContainer>
      <Title level={1} className="main-title" style={{ 
        color: 'white', 
        marginBottom: '24px', 
        fontSize: '52px',
        fontWeight: '700',
        lineHeight: '1.2'
      }}>
        Dual-Flow Recruiting Platform
      </Title>
      <Text className="main-subtitle" style={{ 
        fontSize: '24px', 
        color: 'rgba(255, 255, 255, 0.9)', 
        marginBottom: '60px', 
        display: 'block',
        lineHeight: '1.6',
        maxWidth: '850px',
        margin: '0 auto 60px auto'
      }}>
        Whether you go find candidates or let them come to you, every interaction feeds the same growing pool you own
      </Text>

      {/* Dual Flow Cards */}
      <Row gutter={[40, 40]} style={{ marginBottom: '80px' }}>
        <Col xs={24} md={12}>
          <FlowCard 
            onClick={() => window.location.href = '/for-recruiters'}
            style={{ cursor: 'pointer' }}
          >
            <div style={{ textAlign: 'center', marginBottom: '28px' }}>
              <TeamOutlined style={{ fontSize: '52px', color: '#667eea', marginBottom: '20px' }} />
              <Title level={2} style={{ 
                margin: 0, 
                color: '#667eea', 
                fontSize: '32px',
                fontWeight: '600',
                lineHeight: '1.3'
              }}>
                For Recruiters
              </Title>
            </div>
            
            <Title level={3} style={{ 
              color: '#333', 
              marginBottom: '20px',
              fontSize: '26px',
              fontWeight: '600',
              textAlign: 'center'
            }}>
              Outbound Sourcing
            </Title>
            
            <Text style={{ 
              fontSize: '20px', 
              color: '#666', 
              marginBottom: '32px', 
              display: 'block',
              lineHeight: '1.6',
              textAlign: 'center'
            }}>
              Proactively find and invite the best candidates from your talent pool with AI-powered matching and smart invitations.
            </Text>

            <div style={{ textAlign: 'left' }}>
              <StepCard>
                <Title level={4} style={{ 
                  color: '#333', 
                  marginBottom: '8px',
                  fontSize: '26px',
                  fontWeight: '600',
                  lineHeight: '1.4'
                }}>
                  🎯 Smart Talent Pool
                </Title>
                <Text style={{ 
                  color: '#666',
                  fontSize: '18px',
                  lineHeight: '1.5'
                }}>
                  Every candidate who applies or gets invited joins your talent pool automatically — no manual work, no missed candidates
                </Text>
              </StepCard>

              <StepCard>
                <Title level={4} style={{ 
                  color: '#333', 
                  marginBottom: '8px',
                  fontSize: '26px',
                  fontWeight: '600',
                  lineHeight: '1.4'
                }}>
                  📝 AI Job Descriptions
                </Title>
                <Text style={{ 
                  color: '#666',
                  fontSize: '18px',
                  lineHeight: '1.5'
                }}>
                  Generate a professional, company-specific job description with AI in seconds, then post it to your job board
                </Text>
              </StepCard>

              <StepCard>
                <Title level={4} style={{ 
                  color: '#333', 
                  marginBottom: '8px',
                  fontSize: '26px',
                  fontWeight: '600',
                  lineHeight: '1.4'
                }}>
                  ⚡ Auto-staging
                </Title>
                <Text style={{ 
                  color: '#666',
                  fontSize: '18px',
                  lineHeight: '1.5'
                }}>
                  Candidates move through hiring stages automatically based on decisions—no manual triggers
                </Text>
              </StepCard>

              <StepCard>
                <Title level={4} style={{ 
                  color: '#333', 
                  marginBottom: '8px',
                  fontSize: '26px',
                  fontWeight: '600',
                  lineHeight: '1.4'
                }}>
                  ✉️ AI-Generated Offers
                </Title>
                <Text style={{ 
                  color: '#666',
                  fontSize: '18px',
                  lineHeight: '1.5'
                }}>
                  Generate and share offer letters in under a minute with automatic delivery to all parties
                </Text>
              </StepCard>

            </div>

            <div style={{ textAlign: 'center', marginTop: '32px' }}>
              <ArrowRightOutlined style={{ fontSize: '20px', color: '#667eea' }} />
              <Text style={{ 
                display: 'block', 
                marginTop: '8px', 
                color: '#667eea', 
                fontWeight: '600',
                fontSize: '20px'
              }}>
                Start Recruiting →
              </Text>
            </div>
          </FlowCard>
        </Col>

        <Col xs={24} md={12}>
          <FlowCard 
            onClick={() => window.location.href = '/for-job-seekers'}
            style={{ cursor: 'pointer' }}
          >
            <div style={{ textAlign: 'center', marginBottom: '28px' }}>
              <UserOutlined style={{ fontSize: '52px', color: '#764ba2', marginBottom: '20px' }} />
              <Title level={2} style={{ 
                margin: 0, 
                color: '#764ba2',
                fontSize: '32px',
                fontWeight: '600',
                lineHeight: '1.3'
              }}>
                For Job Seekers
              </Title>
            </div>
            
            <Title level={3} style={{ 
              color: '#333', 
              marginBottom: '20px',
              fontSize: '26px',
              fontWeight: '600',
              textAlign: 'center'
            }}>
              Job Board Discovery
            </Title>
            
            <Text style={{ 
              fontSize: '20px', 
              color: '#666', 
              marginBottom: '32px', 
              display: 'block',
              lineHeight: '1.6',
              textAlign: 'center'
            }}>
              Browse opportunities, get matched with perfect roles, and apply directly to jobs that align with your skills and career goals.
            </Text>

            <div style={{ textAlign: 'left' }}>
              <StepCard>
                <Title level={4} style={{ 
                  color: '#333', 
                  marginBottom: '8px',
                  fontSize: '26px',
                  fontWeight: '600',
                  lineHeight: '1.4'
                }}>
                  📋 Smart Profile
                </Title>
                <Text style={{ 
                  color: '#666',
                  fontSize: '18px',
                  lineHeight: '1.5'
                }}>
                  Create a comprehensive profile with resume and all details—always up-to-date and ready to use
                </Text>
              </StepCard>

              <StepCard>
                <Title level={4} style={{ 
                  color: '#333', 
                  marginBottom: '8px',
                  fontSize: '26px',
                  fontWeight: '600',
                  lineHeight: '1.4'
                }}>
                  ⭐ One-Click Applications
                </Title>
                <Text style={{ 
                  color: '#666',
                  fontSize: '18px',
                  lineHeight: '1.5'
                }}>
                  Apply instantly with your smart profile—no repetitive form filling required
                </Text>
              </StepCard>

              <StepCard>
                <Title level={4} style={{ 
                  color: '#333', 
                  marginBottom: '8px',
                  fontSize: '26px',
                  fontWeight: '600',
                  lineHeight: '1.4'
                }}>
                  👁️ Live Application Tracking
                </Title>
                <Text style={{ 
                  color: '#666',
                  fontSize: '18px',
                  lineHeight: '1.5'
                }}>
                  See exactly where you stand in every application process with real-time updates
                </Text>
              </StepCard>

              <StepCard>
                <Title level={4} style={{ 
                  color: '#333', 
                  marginBottom: '8px',
                  fontSize: '26px',
                  fontWeight: '600',
                  lineHeight: '1.4'
                }}>
                  💬 Actionable Feedback
                </Title>
                <Text style={{ 
                  color: '#666',
                  fontSize: '18px',
                  lineHeight: '1.5'
                }}>
                  Learn from every interaction with specific feedback to improve future applications
                </Text>
              </StepCard>

            </div>

            <div style={{ textAlign: 'center', marginTop: '32px' }}>
              <ArrowRightOutlined style={{ fontSize: '20px', color: '#764ba2' }} />
              <Text style={{ 
                display: 'block', 
                marginTop: '8px', 
                color: '#764ba2', 
                fontWeight: '600',
                fontSize: '20px'
              }}>
                Browse Jobs →
              </Text>
            </div>
          </FlowCard>
        </Col>
      </Row>

      {/* Why Both Flows Matter */}
      <div style={{ textAlign: 'center' }}>
        <Title level={2} style={{ 
          color: 'white', 
          marginBottom: '48px',
          fontSize: '36px',
          fontWeight: '600',
          lineHeight: '1.3',
          alignContent: 'center!important',
          textAlign: 'center!important'
        }}>
          Why Both Flows Matter
        </Title>
        <Row gutter={[32, 32]}>
          <Col xs={24} md={8}>
            <div style={{ padding: '24px 16px' }}>
              <Title level={3} style={{ 
                color: 'white',
                fontSize: '28px',
                fontWeight: '600',
                marginBottom: '16px',
                lineHeight: '1.3'
              }}>
                Better Reach
              </Title>
              <Text style={{ 
                color: 'rgba(255, 255, 255, 0.9)', 
                fontSize: '22px',
                lineHeight: '1.6'
              }}>
                Outbound sourcing finds passive candidates while job board captures active job seekers
              </Text>
            </div>
          </Col>
          <Col xs={24} md={8}>
            <div style={{ padding: '24px 16px' }}>
              <Title level={3} style={{ 
                color: 'white',
                fontSize: '28px',
                fontWeight: '600',
                marginBottom: '16px',
                lineHeight: '1.3'
              }}>
                Higher Quality
              </Title>
              <Text style={{ 
                color: 'rgba(255, 255, 255, 0.9)', 
                fontSize: '20px',
                lineHeight: '1.6'
              }}>
                AI matching ensures the right candidates see the right opportunities in both flows
              </Text>
            </div>
          </Col>
          <Col xs={24} md={8}>
            <div style={{ padding: '24px 16px' }}>
              <Title level={3} style={{ 
                color: 'white',
                fontSize: '28px',
                fontWeight: '600',
                marginBottom: '16px',
                lineHeight: '1.3'
              }}>
                Growing Independence
              </Title>
              <Text style={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '20px',
                lineHeight: '1.6'
              }}>
                Every hire adds to your own pool, so each new role relies a little less on LinkedIn or job boards
              </Text>
            </div>
          </Col>
        </Row>
      </div>
    </JobBoardContainer>
  );
};

export default JobBoardSection;